import { Collection } from './Collection';
import { Face } from './Face';
import { Vertex } from './Vertex';

// https://webglfundamentals.org/webgl/resources/primitives.js

export function createSphere(
  radius: number,
  axisDivisions: number,
  heightDivisions: number,
  includeInnerFaces = true,
): Collection {
  const collection = new Collection();
  const startLatitude = 0;
  const endLatitude = Math.PI;
  const startLongitude = 0;
  const endLongitude = Math.PI * 2;

  const latRange = endLatitude - startLatitude;
  const longRange = endLongitude - startLongitude;

  const vertices: Vertex[][] = [];
  const txCoords = new Map<Vertex, [number, number]>();

  // Generate the vertices by walking spherical coordinates.
  for (let y = 0; y <= heightDivisions; y++) {
    vertices.push([]);
    for (let x = 0; x <= axisDivisions; x++) {
      const u = x / axisDivisions;
      const v = y / heightDivisions;
      const theta = longRange * u;
      const phi = latRange * v;
      const sinTheta = Math.sin(theta);
      const cosTheta = Math.cos(theta);
      const sinPhi = Math.sin(phi);
      const cosPhi = Math.cos(phi);
      const ux = cosTheta * sinPhi;
      const uy = cosPhi;
      const uz = sinTheta * sinPhi;

      const vertex = new Vertex()
        .setPosition([radius * ux, radius * uy, radius * uz])
        .setNormal([ux, uy, uz]);

      txCoords.set(vertex, [1 - u, v]);
      vertices[y].push(vertex);
      collection.addVertex(vertex);
    }
  }

  const setUVs = (face: Face) => {
    face.setTextureOffsets(
      txCoords.get(face.vertices[0])!,
      txCoords.get(face.vertices[1])!,
      txCoords.get(face.vertices[2])!,
    );
  };

  for (let x = 0; x < axisDivisions; x++) {
    for (let y = 0; y < heightDivisions; y++) {
      const faceA = new Face(vertices[y][x], vertices[y][x + 1], vertices[y + 1][x]);
      setUVs(faceA);

      const faceB = new Face(vertices[y + 1][x], vertices[y][x + 1], vertices[y + 1][x + 1]);
      setUVs(faceB);

      collection.addFace(faceA).addFace(faceB);

      if (includeInnerFaces) {
        const faceC = new Face(vertices[y + 1][x], vertices[y][x + 1], vertices[y][x]);
        setUVs(faceC);

        const faceD = new Face(vertices[y + 1][x + 1], vertices[y][x + 1], vertices[y + 1][x]);
        setUVs(faceD);

        collection.addFace(faceC).addFace(faceD);
      }
    }
  }

  return collection;
}

export function createCube(size: number, includeInnerFaces = true): Collection {
  const collection = new Collection();
  const k = size / 2;

  const indices = [
    [3, 7, 5, 1],
    [6, 2, 0, 4],
    [6, 7, 3, 2],
    [0, 1, 5, 4],
    [7, 6, 4, 5],
    [2, 3, 1, 0],
  ];

  const corners: [number, number, number][] = [
    [-k, -k, -k],
    [+k, -k, -k],
    [-k, +k, -k],
    [+k, +k, -k],
    [-k, -k, +k],
    [+k, -k, +k],
    [-k, +k, +k],
    [+k, +k, +k],
  ];

  const uv: [number, number][] = [
    [1, 0],
    [0, 0],
    [0, 1],
    [1, 1],
  ];

  for (let f = 0; f < 6; f++) {
    const verts = indices[f].map((i) => new Vertex().setPosition(corners[i]));
    collection.addVertices(verts);

    const faceA = new Face(verts[0], verts[1], verts[2])
      .useFlatNormals()
      .setTextureOffsets(uv[0], uv[1], uv[1]);

    const faceB = new Face(verts[2], verts[3], verts[0])
      .useFlatNormals()
      .setTextureOffsets(uv[2], uv[3], uv[0]);

    collection.addFace(faceA).addFace(faceB);

    if (includeInnerFaces) {
      const inner = indices[f].map((i) => new Vertex().setPosition(corners[i]));
      collection.addVertices(inner);

      const faceC = new Face(inner[2], inner[1], inner[0])
        .useFlatNormals()
        .setTextureOffsets(uv[2], uv[1], uv[0]);

      const faceD = new Face(inner[0], inner[3], inner[2])
        .useFlatNormals()
        .setTextureOffsets(uv[0], uv[3], uv[2]);

      collection.addFace(faceC).addFace(faceD);
    }
  }

  return collection;
}

/**
 * Creates a cylinder (or cone when topRadius=0) along the Y axis from y=0 to y=height.
 */
export function createCylinder(
  bottomRadius: number,
  topRadius: number,
  height: number,
  segments: number,
): Collection {
  const collection = new Collection();
  const isCone = topRadius < 0.0001;

  // Bottom ring
  const bottomVerts: Vertex[] = [];
  for (let i = 0; i <= segments; i++) {
    const theta = (i / segments) * Math.PI * 2;
    const x = Math.cos(theta) * bottomRadius;
    const z = Math.sin(theta) * bottomRadius;
    const v = new Vertex().setPosition([x, 0, z]);
    bottomVerts.push(v);
    collection.addVertex(v);
  }

  // Top ring (or single apex for cone)
  const topVerts: Vertex[] = [];
  if (isCone) {
    const apex = new Vertex().setPosition([0, height, 0]);
    collection.addVertex(apex);
    topVerts.push(apex);
  } else {
    for (let i = 0; i <= segments; i++) {
      const theta = (i / segments) * Math.PI * 2;
      const x = Math.cos(theta) * topRadius;
      const z = Math.sin(theta) * topRadius;
      const v = new Vertex().setPosition([x, height, z]);
      topVerts.push(v);
      collection.addVertex(v);
    }
  }

  // Side faces
  for (let i = 0; i < segments; i++) {
    if (isCone) {
      collection.addFace(new Face(bottomVerts[i], bottomVerts[i + 1], topVerts[0]).useFlatNormals());
    } else {
      collection.addFace(new Face(bottomVerts[i], bottomVerts[i + 1], topVerts[i]).useFlatNormals());
      collection.addFace(new Face(topVerts[i], bottomVerts[i + 1], topVerts[i + 1]).useFlatNormals());
    }
  }

  // Bottom cap
  const bottomCenter = new Vertex().setPosition([0, 0, 0]);
  collection.addVertex(bottomCenter);
  for (let i = 0; i < segments; i++) {
    collection.addFace(new Face(bottomCenter, bottomVerts[i + 1], bottomVerts[i]).useFlatNormals());
  }

  // Top cap (only for cylinders, not cones)
  if (!isCone) {
    const topCenter = new Vertex().setPosition([0, height, 0]);
    collection.addVertex(topCenter);
    for (let i = 0; i < segments; i++) {
      collection.addFace(new Face(topCenter, topVerts[i], topVerts[i + 1]).useFlatNormals());
    }
  }

  return collection;
}
